import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import axios from "axios";
import Cookies from "universal-cookie";
import { createSandboxClub } from "./api";
import DialogHeader from "./DialogHeader";

interface MyHandler {
  id: number;
  first_name: string;
  last_name: string;
}

interface CreateClubDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
}

const CreateClubDialog: React.FC<CreateClubDialogProps> = ({
  open,
  onClose,
  onSuccess,
  onError,
}) => {
  const [name, setName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [needsHandlerName, setNeedsHandlerName] = useState(false);
  const [loadingHandlers, setLoadingHandlers] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;

    setName("");
    setFirstName("");
    setLastName("");
    setNeedsHandlerName(false);

    const loadHandlers = async () => {
      try {
        setLoadingHandlers(true);
        const token = new Cookies().get("token");
        const { data } = await axios.get<MyHandler[]>(
          "https://api.easyagility.co.uk/my-handlers",
          { headers: { Authorization: "Bearer " + token } },
        );
        setNeedsHandlerName(!data || data.length === 0);
      } catch {
        setNeedsHandlerName(true);
      } finally {
        setLoadingHandlers(false);
      }
    };

    loadHandlers();
  }, [open]);

  const canSubmit =
    name.trim().length > 0 &&
    (!needsHandlerName ||
      (firstName.trim().length > 0 && lastName.trim().length > 0));

  const handleCreate = async () => {
    if (!canSubmit) return;

    try {
      setCreating(true);
      await createSandboxClub({
        name: name.trim(),
        ...(needsHandlerName
          ? { first_name: firstName.trim(), last_name: lastName.trim() }
          : {}),
      });
      onSuccess();
    } catch (err: unknown) {
      console.error("Error creating club:", err);
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 409) {
        onError("You already have a sandbox club. Visit Club Administration to manage it.");
      } else {
        onError("Failed to create club. Please try again.");
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogHeader
        title="Set Up Your Club"
        onClose={onClose}
        closeDisabled={creating}
      />
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Your club starts in sandbox mode so you can try everything safely.
            Member emails are sent to you until you go live.
          </Alert>
          <TextField
            fullWidth
            label="Club name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Riverside Agility Club"
            variant="outlined"
            disabled={creating || loadingHandlers}
          />
          {needsHandlerName && (
            <>
              <TextField
                fullWidth
                label="Your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                variant="outlined"
                disabled={creating || loadingHandlers}
              />
              <TextField
                fullWidth
                label="Your last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                variant="outlined"
                disabled={creating || loadingHandlers}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={creating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={creating || loadingHandlers || !canSubmit}
          startIcon={
            creating ? <CircularProgress size={20} /> : <Add />
          }
        >
          {creating ? "Creating…" : "Create Club"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateClubDialog;
